import type {
  DraftState,
  PoolSuggestion,
  QuestionAnswer,
  WizardItemType,
  WizardSampleGroup
} from '../../Model/OrderWizard/OrderWizardDraft.ts'
import type { PoolContents, PoolTarget } from './pools.ts'
import { compactPools, currentPools, nextPoolAfter, placedGroups, poolContents } from './pools.ts'
import { sampleDisplayName, sheetNameOf } from './sampleLabels.ts'
import { groupWriteError } from './writeState.ts'
import { missingReverse } from './reverseFiles.ts'

/**
 * The pool board's local edit: each item's pool, and the pools shown as columns. A pool emptied while
 * editing keeps its column, so the numbers do not shift under the pointer; it goes on apply.
 */
export type Board = {
  pools: Record<string, number>
  columns: number[]
}

export const boardOf = (pools: Record<string, number>): Board => ({
  pools,
  columns: [...new Set(Object.values(pools))].sort((a, b) => a - b)
})

export const moveItems = (board: Board, keys: readonly string[], target: PoolTarget): Board => {
  if (keys.length === 0) {
    return board
  }
  const pool = target === 'new' ? nextPoolAfter(board.columns) : target
  const pools = { ...board.pools }
  for (const key of keys) {
    pools[key] = pool
  }
  return { pools, columns: board.columns.includes(pool) ? board.columns : [...board.columns, pool].sort((a, b) => a - b) }
}

export type Selection = {
  keys: ReadonlySet<string>
  /** Where a shift-click range starts: the last item clicked without shift. */
  anchor?: string
}

export const NO_SELECTION: Selection = { keys: new Set() }

export type ClickModifiers = { shift: boolean; toggle: boolean }

const rangeOf = (column: readonly string[], from: string, to: string): string[] => {
  const start = column.indexOf(from)
  const end = column.indexOf(to)
  return column.slice(Math.min(start, end), Math.max(start, end) + 1)
}

/**
 * A click on an item, `column` being its column's keys in file order: plain selects only it, ctrl/cmd
 * toggles it, shift selects the range from the anchor in the same column (added with ctrl/cmd too).
 * A shift-click with the anchor in another column selects just the item.
 */
export const clickSelection = (
  selection: Selection,
  key: string,
  column: readonly string[],
  { shift, toggle }: ClickModifiers
): Selection => {
  if (shift && selection.anchor !== undefined && column.includes(selection.anchor)) {
    const range = rangeOf(column, selection.anchor, key)
    return { keys: new Set(toggle ? [...selection.keys, ...range] : range), anchor: selection.anchor }
  }
  if (toggle) {
    const keys = new Set(selection.keys)
    if (keys.has(key)) {
      keys.delete(key)
    } else {
      keys.add(key)
    }
    return { keys, anchor: key }
  }
  return { keys: new Set([key]), anchor: key }
}

/** A checkbox: toggles its item, or with shift adds the range from the anchor. */
export const checkboxSelection = (selection: Selection, key: string, column: readonly string[], shift: boolean): Selection =>
  clickSelection(selection, key, column, { shift, toggle: true })

/** What a drag from `pressed` carries: the whole selection when the item is in it, else just the item. */
export const draggedKeys = (selection: Selection, pressed: string): string[] =>
  selection.keys.has(pressed) ? [...selection.keys] : [pressed]

const SEPARATOR = /[-_.\s]/

/**
 * The start every name shares, cut back to just after a separator so no name loses part of a token,
 * and short of the shortest name so none is left empty; empty for a single name or when what is shared
 * is shorter than `minimum`.
 */
export const sharedPrefix = (names: readonly string[], minimum = 4): string => {
  if (names.length < 2) {
    return ''
  }
  let length = names[0].length
  for (const name of names) {
    length = Math.min(length, name.length)
    for (let i = 0; i < length; i++) {
      if (name[i] !== names[0][i]) {
        length = i
        break
      }
    }
  }
  const shortest = Math.min(...names.map((name) => name.length))
  let cut = Math.min(length, shortest - 1)
  while (cut > 0 && !SEPARATOR.test(names[0][cut - 1])) {
    cut--
  }
  return cut >= minimum ? names[0].slice(0, cut) : ''
}

/** A name as a row shows it: without `prefix` (which every item shares), marked by a leading ellipsis. */
export const shownName = (name: string, prefix: string): string =>
  prefix && name.startsWith(prefix) && name.length > prefix.length ? `…${name.slice(prefix.length)}` : name

/** A name in the pieces it may wrap between: after each separator, so a line breaks between tokens. */
export const nameParts = (name: string): string[] => name.split(/(?<=[-_.])/)

type PoolsAnswer = Extract<QuestionAnswer, { kind: 'pools' }>

/** The items the board holds, in file order. */
export const boardOrder = (draft: DraftState): string[] => placedGroups(draft).map((group) => group.key)

/** Each item's pool with `edit` laid over `committed`. */
export const overlayEdit = (committed: Readonly<Record<string, number>>, edit: Board, order: readonly string[]): Record<string, number> =>
  Object.fromEntries(order.map((key) => [key, edit.pools[key] ?? committed[key] ?? 0]))

/** Each item's pool with `edit` laid over the pools as the draft has them. */
export const editedPools = (draft: DraftState, edit: Board, order: readonly string[] = boardOrder(draft)): Record<string, number> =>
  overlayEdit(currentPools(draft), edit, order)

/** The pools answer "Use these pools" gives: one pool, the file names' suggestion unchanged, or entered pools. */
export const appliedAnswer = (draft: DraftState, edit: Board): PoolsAnswer => {
  const order = boardOrder(draft)
  const pools = compactPools(editedPools(draft, edit, order))
  const suggestion = draft.questions.find((question) => question.kind === 'pools')?.suggestion
  const offered = suggestion && compactPools(suggestion.pools)
  if (new Set(Object.values(pools)).size <= 1) {
    return { kind: 'pools', choice: 'one' }
  }
  if (suggestion && offered && order.every((key) => offered[key] === pools[key])) {
    return { kind: 'pools', choice: 'assign', pools, source: 'suggested', method: suggestion.method }
  }
  return { kind: 'pools', choice: 'assign', pools, source: 'manual' }
}

/** The edit after a move: `undefined` once the pools are back to what the draft has. */
export const editAfterMove = (
  board: Board,
  keys: readonly string[],
  target: PoolTarget,
  order: readonly string[],
  committed: Readonly<Record<string, number>>
): Board | undefined => {
  const next = moveItems(board, keys, target)
  const pools = compactPools(Object.fromEntries(order.map((key) => [key, next.pools[key] ?? 0])))
  return order.some((key) => pools[key] !== committed[key]) ? next : undefined
}

export type MarkAction = { label: string; groupKeys: string[]; itemType: WizardItemType }

/** The selection bar's mark: back to samples when all are controls, else the samples among them become controls. */
export const markAction = (keys: readonly string[], controls: ReadonlySet<string>): MarkAction => {
  const allControls = keys.length > 0 && keys.every((key) => controls.has(key))
  if (allControls) {
    return { label: 'Mark as sample', groupKeys: [...keys], itemType: 'SAMPLE' }
  }
  const someControls = keys.some((key) => controls.has(key))
  return {
    label: someControls ? 'Mark all as negative controls' : 'Mark as negative control',
    groupKeys: keys.filter((key) => !controls.has(key)),
    itemType: 'NEGATIVE_CONTROL'
  }
}

/** "Keep" on the pools question: the pools as the draft has them. */
export const keptAnswer = (draft: DraftState): PoolsAnswer => {
  const pools = currentPools(draft)
  return poolContents(draft, pools).length > 1
    ? { kind: 'pools', choice: 'assign', pools, source: 'manual' }
    : { kind: 'pools', choice: 'one' }
}

export const suggestedAnswer = (suggestion: PoolSuggestion): PoolsAnswer => ({
  kind: 'pools',
  choice: 'assign',
  pools: suggestion.pools,
  source: 'suggested',
  method: suggestion.method
})

/** "Edit them first": the suggestion laid over the pools, as unapplied changes. */
export const suggestionEdit = (draft: DraftState, suggestion: PoolSuggestion | undefined): Board | undefined =>
  suggestion ? boardOf(compactPools({ ...currentPools(draft), ...suggestion.pools })) : undefined

export type BoardLayout = 'list' | 'board'

/** One list while the order is one pool; the board once there is anything to split. */
export const boardLayout = (state: { split: boolean; several: boolean; pending: boolean; suggested: boolean }): BoardLayout =>
  state.split || state.several || state.pending || state.suggested ? 'board' : 'list'

/** What the board shows of each item, apart from the draft's other state. */
export type BoardItems = {
  /** In file order. */
  order: string[]
  names: Record<string, string>
  /** The tooltip: the full name, and what else there is to know about the item. */
  titles: Record<string, string>
  controls: ReadonlySet<string>
  /** Controls recognised from their names rather than marked by the lab. */
  autoControls: ReadonlySet<string>
  /** Items whose write into the order failed. */
  errors: ReadonlySet<string>
  /** A short state shown on the item: its R2 missing, or set in the classic view. */
  tags: Record<string, string>
  /** Each item's pool as the draft has it, compact. */
  pools: Record<string, number>
}

const ITEM_STATUS_TEXT: Partial<Record<string, string>> = { waiting: 'waiting for upload', creating: 'creating…' }

const titleOf = (draft: DraftState, group: WizardSampleGroup, name: string): string => {
  const notes = [
    draft.sheet && !sheetNameOf(draft, group.key) ? 'name from the file' : undefined,
    ITEM_STATUS_TEXT[group.item.status],
    groupWriteError(group)
  ].filter(Boolean)
  return notes.length > 0 ? `${name} (${notes.join('; ')})` : name
}

const tagOf = (draft: DraftState, group: WizardSampleGroup): string | undefined => {
  if (missingReverse(draft, group)) {
    return 'R2 missing'
  }
  return group.reverseFromClassic ? 'R2 changed in the classic view' : undefined
}

export const boardItems = (draft: DraftState): BoardItems => {
  const order = boardOrder(draft)
  const names = Object.fromEntries(order.map((key) => [key, sampleDisplayName(draft, key)]))
  const controls = new Set(order.filter((key) => draft.groups[key].itemType === 'NEGATIVE_CONTROL'))
  return {
    order,
    names,
    titles: Object.fromEntries(order.map((key) => [key, titleOf(draft, draft.groups[key], names[key])])),
    controls,
    autoControls: new Set([...controls].filter((key) => draft.groups[key].typeSource === 'auto')),
    errors: new Set(order.filter((key) => !!groupWriteError(draft.groups[key]))),
    tags: Object.fromEntries(
      order.flatMap((key) => {
        const tag = tagOf(draft, draft.groups[key])
        return tag ? [[key, tag]] : []
      })
    ),
    pools: currentPools(draft)
  }
}

export const sameSet = (a: ReadonlySet<string>, b: ReadonlySet<string>): boolean => a.size === b.size && [...a].every((key) => b.has(key))

const sameRecord = <T>(a: Readonly<Record<string, T>>, b: Readonly<Record<string, T>>): boolean => {
  const keys = Object.keys(a)
  return keys.length === Object.keys(b).length && keys.every((key) => a[key] === b[key])
}

export const sameBoardItems = (a: BoardItems, b: BoardItems): boolean =>
  a.order.length === b.order.length &&
  a.order.every((key, index) => b.order[index] === key) &&
  sameRecord(a.names, b.names) &&
  sameRecord(a.titles, b.titles) &&
  sameRecord(a.tags, b.tags) &&
  sameRecord(a.pools, b.pools) &&
  sameSet(a.controls, b.controls) &&
  sameSet(a.autoControls, b.autoControls) &&
  sameSet(a.errors, b.errors)

const NO_KEYS: ReadonlySet<string> = new Set()

/** The selection split by column, each column keeping its previous set while its part is unchanged. */
export const selectionByColumn = (
  keys: ReadonlySet<string>,
  contents: readonly PoolContents[],
  previous: ReadonlyMap<number, ReadonlySet<string>> = new Map()
): Map<number, ReadonlySet<string>> =>
  new Map(
    contents.map((pool) => {
      const selected = new Set(pool.keys.filter((key) => keys.has(key)))
      const before = previous.get(pool.pool)
      return [pool.pool, before && sameSet(before, selected) ? before : selected.size === 0 ? NO_KEYS : selected]
    })
  )
